import { useCallback, useEffect, useRef, useState } from "react";
import { useAnmeldung } from "../zustand/Anmeldung";
import { geschuetzteAdresse, holeDateien, ladeDateiHoch, loescheDatei } from "../dienst/server";
import useDateiZugang from "../haken/useDateiZugang";
import { Abschnitt } from "../bausteine/Grundelemente";
import { Dokument, Kreuz, Schloss, Warnung } from "../bausteine/Symbole";
import { kurzesDatum, uhrzeit } from "../hilfen/datum";

/*
  Dateien und Akten — das Lager der Fraktion.

  Hier liegt ausschließlich Hochgeladenes: PDF, Bilder, Fremdes. Man legt es
  ab, findet es wieder, lädt es herunter.

  Was die Fraktion selbst SCHREIBT, steht nicht mehr hier, sondern in
  Office. Kurze Zeit lag beides nebeneinander — das war der falsche Schnitt:
  Eine Datei ist ein Gegenstand und gehört an einen Ort; ein Dokument ist
  Arbeit, und die sucht man nicht am Ort, sondern nach dem Zeitpunkt.

  „Akten“ steht schon im Namen, ist aber noch keine Funktion. Ordner, in
  denen die Unterlagen zu einem Vorgang zusammenliegen, kommen als eigenes
  Stück — dann löst dieser Bereich ein, was das Standbild „Interne
  Dokumentendatenbank“ verspricht.

  Nicht zu verwechseln mit den Verwaltungsvorlagen auf dem Dashboard: Die
  kommen über die OParl-Schnittstelle aus dem Ratsinformationssystem und
  lassen sich nicht ändern. Was hier liegt, gehört der Fraktion.
*/

const HOECHSTGROESSE = 25 * 1024 * 1024;

export default function Dateien() {
  const { token, konto, fraktion } = useAnmeldung();
  const zugang = useDateiZugang();

  const [dateien, setzeDateien] = useState([]);
  const [laedt, setzeLaedt] = useState(true);
  const [fehler, setzeFehler] = useState("");
  const [laeuftHoch, setzeLaeuftHoch] = useState(false);
  const dateiWaehler = useRef(null);

  const laden = useCallback(async () => {
    setzeFehler("");
    try {
      setzeDateien(await holeDateien(token));
    } catch (f) {
      setzeFehler(f.message);
    } finally {
      setzeLaedt(false);
    }
  }, [token]);

  useEffect(() => {
    laden();
  }, [laden]);

  async function hochladen(e) {
    const datei = e.target.files && e.target.files[0];
    if (!datei) return;

    if (datei.size > HOECHSTGROESSE) {
      setzeFehler(
        `„${datei.name}" ist ${Math.round(datei.size / 1048576)} MB groß. Mehr als 25 MB gehen nicht.`
      );
      e.target.value = "";
      return;
    }

    setzeFehler("");
    setzeLaeuftHoch(true);
    try {
      await ladeDateiHoch(token, {
        titel: datei.name.replace(/\.[^.]+$/, ""),
        datei,
        fraktionId: konto.fraktion,
        kontoId: konto.id,
        nichtoeffentlich: false,
      });
      await laden();
    } catch (f) {
      setzeFehler(f.message);
    } finally {
      setzeLaeuftHoch(false);
      e.target.value = "";
    }
  }

  async function entfernen(satz) {
    if (!window.confirm(`„${satz.titel}" wirklich löschen? Das lässt sich nicht rückgängig machen.`))
      return;
    try {
      await loescheDatei(token, satz.id);
      await laden();
    } catch (f) {
      setzeFehler(f.message);
    }
  }

  if (!konto.fraktion) {
    return (
      <div className="karte" style={{ marginTop: "var(--luft-5)" }}>
        <p className="tor__fehler">
          <Warnung groesse={18} />
          <span>
            Dein Konto ist keiner Fraktion zugeordnet. Deshalb siehst du hier
            nichts. Melde dich bei uns, dann richten wir das ein.
          </span>
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="detailkopf">
        <p className="detailkopf__vorspann">
          {fraktion ? `${fraktion.name} · ${fraktion.kommune}` : "Fraktion"}
        </p>
        <h1 className="detailkopf__titel">Dateien und Akten</h1>
        <p className="leise" style={{ marginTop: "var(--luft-2)" }}>
          Hochgeladene Unterlagen der Fraktion. Alle Mitglieder deiner Fraktion
          sehen sie — sonst niemand. Was ihr selbst schreibt, steht in Office.
        </p>
      </div>

      <div className="ablageleiste">
        <input
          ref={dateiWaehler}
          type="file"
          onChange={hochladen}
          style={{ display: "none" }}
        />
        <button
          className="knopf"
          onClick={() => dateiWaehler.current && dateiWaehler.current.click()}
          disabled={laeuftHoch}
        >
          <Dokument groesse={18} />
          {laeuftHoch ? "Wird hochgeladen …" : "Datei hochladen"}
        </button>
        <span className="klein leise">Bis 25 MB je Datei.</span>
      </div>

      {fehler && (
        <p className="tor__fehler" style={{ marginTop: "var(--luft-4)" }} role="alert">
          <Warnung groesse={18} />
          <span>{fehler}</span>
        </p>
      )}

      <Abschnitt
        titel={laedt ? "Wird geladen …" : `Abgelegt · ${dateien.length}`}
        hinweis={
          !laedt && dateien.length === 0
            ? "Noch nichts abgelegt. Die erste Datei landet hier."
            : undefined
        }
      >
        {dateien.map((satz) => (
          <Zeile
            key={satz.id}
            satz={satz}
            eigen={satz.hochgeladen_von === konto.id}
            zugang={zugang}
            aufEntfernen={() => entfernen(satz)}
          />
        ))}
      </Abschnitt>
    </>
  );
}

function Zeile({ satz, eigen, zugang, aufEntfernen }) {
  /*
    Ohne Zugang keine Adresse. Die hochgeladenen Dateien sind geschützt —
    eine Adresse ohne Token führte nur in ein leeres Fenster mit einer
    Fehlermeldung, und das sähe nach einer kaputten Datei aus statt nach
    einem Token, das gleich da ist.
  */
  const adresse = geschuetzteAdresse(zugang, satz, "datei");
  const wer =
    satz.expand && satz.expand.hochgeladen_von
      ? satz.expand.hochgeladen_von.name
      : "unbekannt";

  return (
    <div className="aufgabe">
      <span style={{ color: "var(--akzent-kraeftig)", marginTop: "0.15rem" }}>
        {satz.nichtoeffentlich ? <Schloss groesse={20} /> : <Dokument groesse={20} />}
      </span>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="aufgabe__titel">{satz.titel}</div>
        <p className="klein leise" style={{ marginTop: "0.25rem" }}>
          {eigen ? "Von dir" : `Von ${wer}`} · {kurzesDatum(satz.hochgeladen)},{" "}
          {uhrzeit(satz.hochgeladen)}
        </p>

        <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--luft-2)", marginTop: "var(--luft-3)" }}>
          {adresse ? (
            <a
              className="knopf knopf--zurueckhaltend"
              href={adresse}
              target="_blank"
              rel="noopener noreferrer"
              style={{ textDecoration: "none" }}
            >
              Öffnen
            </a>
          ) : (
            <span className="knopf knopf--zurueckhaltend" aria-disabled="true">
              Wird vorbereitet …
            </span>
          )}
          {eigen && (
            <button className="knopf knopf--schlicht" onClick={aufEntfernen}>
              <Kreuz groesse={16} />
              Löschen
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
